<template>
  <div class="customer-panel">
    <div class="customer-card">
      <div class="customer-avatar">{{ customerInitials(selectedCustomer) }}</div>
      <div class="customer-meta">
        <strong>{{ customerName }}</strong>
        <span>{{ $t('retailSale.customer.pointsStatus', { points: 0 }) }}</span>
      </div>
      <button type="button" @click="openPicker">
        <i class="fas fa-user-search"></i>
        {{ $t('retailSale.customer.change') }}
        <kbd>F6</kbd>
      </button>
      <button
        type="button"
        class="notes-inline-button"
        @click="$emit('notes')"
      >
        <i class="fas fa-clipboard"></i>
        {{ $t('retailSale.cart.notes') }}
        <kbd>F7</kbd>
      </button>
    </div>

    <a-modal
      v-model="customerPickerVisible"
      :title="$t('retailSale.customer.selectTitle')"
      :footer="null"
      @cancel="closePicker"
    >
      <div class="customer-picker-summary">
        <span class="customer-picker-summary-icon">
          <i class="fas fa-user-check"></i>
        </span>
        <span class="customer-picker-summary-copy">
          <small>{{ $t('retailSale.customer.current') }}</small>
          <strong>{{ customerName }}</strong>
        </span>
        <span v-if="selectedCustomer" class="customer-picker-summary-code">
          {{ selectedCustomer.customer_code }}
        </span>
      </div>

      <div class="customer-search">
        <i class="fas fa-search"></i>
        <input
          ref="customerSearchInput"
          v-model="search"
          type="search"
          :placeholder="$t('retailSale.customer.searchPlaceholder')"
        />
        <button
          v-if="search"
          type="button"
          class="customer-search-clear"
          :aria-label="$t('retailSale.customer.clearSearch')"
          @click="clearSearch"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="customer-results-header">
        <span>{{ $t('retailSale.customer.results', { count: filteredCustomers.length }) }}</span>
        <kbd>F6</kbd>
      </div>

      <div v-if="filteredCustomers.length" class="customer-options">
        <button
          v-for="customer in filteredCustomers"
          :key="customer.customer_id"
          type="button"
          class="customer-option"
          :class="{
            active:
              selectedCustomer &&
              selectedCustomer.customer_id === customer.customer_id
          }"
          @click="selectCustomer(customer)"
        >
          <span class="customer-option-avatar">
            {{ customerInitials(customer) }}
          </span>
          <span class="customer-option-copy">
            <strong>{{ customerDisplayName(customer) }}</strong>
            <small>{{ customer.customer_code }}</small>
          </span>
          <i v-if="isSelected(customer)" class="fas fa-check"></i>
        </button>
      </div>

      <div v-else class="customer-empty">
        <i class="fas fa-user-slash"></i>
        <strong>
          {{
            search
              ? $t('retailSale.customer.noMatches')
              : $t('retailSale.customer.noCustomers')
          }}
        </strong>
        <small v-if="search">{{ search }}</small>
      </div>
    </a-modal>
  </div>
</template>

<script>
export default {
  name: 'CustomerPanel',
  props: {
    customerName: {
      type: String,
      default: ''
    },
    customers: {
      type: Array,
      default: () => []
    },
    selectedCustomer: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      customerPickerVisible: false,
      search: ''
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleShortcut)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleShortcut)
    this.setPickerState(false)
  },
  watch: {
    customerPickerVisible: {
      immediate: true,
      handler(value) {
        this.setPickerState(value)
      }
    }
  },
  computed: {
    filteredCustomers() {
      const search = this.search.trim().toLowerCase()

      if (!search) return this.customers

      return this.customers.filter(customer => {
        return [customer.customer_name, customer.customer_code, customer.contact_number]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(search))
      })
    }
  },
  methods: {
    setPickerState(visible) {
      if (typeof document === 'undefined') return

      document.body.classList.toggle('retail-customer-picker-open', visible)
    },
    handleShortcut(event) {
      if (event.key !== 'F6') return

      event.preventDefault()
      this.openPicker()
    },
    openPicker() {
      this.customerPickerVisible = true
      this.$nextTick(() => {
        if (!this.$refs.customerSearchInput) return

        this.$refs.customerSearchInput.focus()
        this.$refs.customerSearchInput.select()
      })
    },
    clearSearch() {
      this.search = ''
      this.$nextTick(() => this.$refs.customerSearchInput.focus())
    },
    customerDisplayName(customer) {
      return customer
        ? customer.customer_name || customer.customer_code || ''
        : ''
    },
    customerInitials(customer) {
      const name = this.customerDisplayName(customer).trim()
      const initials = name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')

      return initials || 'C'
    },
    isSelected(customer) {
      return Boolean(
        this.selectedCustomer &&
          this.selectedCustomer.customer_id === customer.customer_id
      )
    },
    selectCustomer(customer) {
      this.$emit('select', customer)
      this.closePicker()
    },
    closePicker() {
      this.customerPickerVisible = false
      this.search = ''
    }
  }
}
</script>

<style src="../../styles/customer-panel.css"></style>
